import { useEffect, useState } from "react";
import { api, type VitalsResult } from "../api";
import { useProjects } from "../project-context";

const RANGES = ["24h", "7d", "30d", "90d"] as const;
type Range = (typeof RANGES)[number];
type Metric = "lcp" | "inp" | "cls" | "ttfb" | "fcp";

const META: Record<Metric, { label: string; desc: string; th: [number, number] }> = {
  lcp: { label: "LCP", desc: "Largest Contentful Paint", th: [2500, 4000] },
  inp: { label: "INP", desc: "Interaction to Next Paint", th: [200, 500] },
  cls: { label: "CLS", desc: "Cumulative Layout Shift", th: [0.1, 0.25] },
  ttfb: { label: "TTFB", desc: "Time to First Byte", th: [800, 1800] },
  fcp: { label: "FCP", desc: "First Contentful Paint", th: [1800, 3000] },
};

const RC = { good: "var(--lime)", ni: "#e0a23b", poor: "var(--coral)", none: "var(--faint)" };

function rate(m: Metric, v: number | null): keyof typeof RC {
  if (v == null) return "none";
  const [g, p] = META[m].th;
  return v <= g ? "good" : v <= p ? "ni" : "poor";
}
function fmt(m: Metric, v: number | null): string {
  if (v == null) return "—";
  if (m === "cls") return v.toFixed(2);
  return v >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${Math.round(v)} ms`;
}
const RATING_LABEL = { good: "bra", ni: "ok", poor: "dårlig", none: "ingen data" };

export function Vitals() {
  const { selected, loading: projLoading } = useProjects();
  const [range, setRange] = useState<Range>("7d");
  const [data, setData] = useState<VitalsResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.vitals(selected.id, range).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [selected, range]);

  if (projLoading) return <div className="spin" />;
  if (!selected) {
    return (
      <div className="card reveal" style={{ marginTop: 40 }}>
        <h3 className="card-title">Ingen prosjekter</h3>
      </div>
    );
  }

  const p75 = data?.p75;
  const samples = p75?.samples ?? 0;

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">{selected.name}</span>
          <h1 className="page-title">Ytelse</h1>
        </div>
        <div className="segment">
          {RANGES.map((r) => (
            <button key={r} className={r === range ? "on" : ""} onClick={() => setRange(r)}>{r}</button>
          ))}
        </div>
      </div>

      {samples === 0 ? (
        <div className="card reveal">
          <div className="empty">
            ingen Web Vitals-målinger ennå{loading ? " (laster …)" : ""}
            <div style={{ marginTop: 10, color: "var(--faint)" }}>
              SDK-en samler LCP/INP/CLS/TTFB/FCP automatisk fra ekte besøk.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid reveal" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", marginBottom: 16 }}>
            {(Object.keys(META) as Metric[]).map((m) => {
              const v = p75?.[m] ?? null;
              const r = rate(m, v);
              return (
                <div className="card stat" key={m}>
                  <div className="eyebrow">{META[m].label}</div>
                  <div className="value" style={{ color: RC[r] }}>{fmt(m, v)}</div>
                  <div className="delta" title={META[m].desc}>p75 · {RATING_LABEL[r]}</div>
                </div>
              );
            })}
          </div>

          <div className="card reveal">
            <h3 className="card-title">Per side (p75) <span className="mono" style={{ fontSize: 11, color: "var(--faint)" }}>{samples} målinger</span></h3>
            <div className="bars">
              <div className="bar-row" style={{ color: "var(--faint)", fontFamily: "var(--mono)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                <span className="label">Sti</span>
                <span style={{ display: "flex", gap: 24 }}><span style={{ width: 60, textAlign: "right" }}>LCP</span><span style={{ width: 60, textAlign: "right" }}>INP</span><span style={{ width: 50, textAlign: "right" }}>CLS</span></span>
              </div>
              {(data?.pages ?? []).map((pg, i) => (
                <div className="bar-row" key={i}>
                  <span className="label">{pg.path}</span>
                  <span className="mono" style={{ display: "flex", gap: 24, fontSize: 12.5 }}>
                    <span style={{ width: 60, textAlign: "right", color: RC[rate("lcp", pg.lcp)] }}>{fmt("lcp", pg.lcp)}</span>
                    <span style={{ width: 60, textAlign: "right", color: RC[rate("inp", pg.inp)] }}>{fmt("inp", pg.inp)}</span>
                    <span style={{ width: 50, textAlign: "right", color: RC[rate("cls", pg.cls)] }}>{fmt("cls", pg.cls)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
